
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAdminNotices } from "@/hooks/use-admin-queries";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { adminApi } from "@/lib/api/admin";
import { getAdminPassword } from "@/lib/auth/storage";
import { formatDateDisplay } from "@/lib/timezone";
import type { NoticeApprovalStatus } from "@/lib/types";

const APPROVAL_BADGE: Record<
  NoticeApprovalStatus,
  { label: string; variant: "default" | "secondary" | "outline" }
> = {
  submitted: { label: "Pending review", variant: "default" },
  approved: { label: "Approved (O)", variant: "secondary" },
  rejected: { label: "Rejected (A)", variant: "outline" },
};

export default function NoticesInboxPage() {
  const queryClient = useQueryClient();
  const adminPassword = getAdminPassword();
  const { data, isLoading, error } = useAdminNotices();

  const decide = useMutation({
    mutationFn: ({
      id,
      decision,
    }: {
      id: string;
      decision: "approve" | "reject";
    }) => adminApi.decideNotice(adminPassword!, id, decision),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "notices"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "register"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "dashboard"] });
    },
  });

  if (isLoading) {
    return <p className="text-muted-foreground">Loading notices…</p>;
  }

  if (error || !data) {
    return <p className="text-red-600">Could not load notices.</p>;
  }

  const pending = data.filter((n) => n.approval_status === "submitted");

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-semibold">Notices inbox</h2>
        <p className="text-sm text-muted-foreground">
          Review absence notices — approve marks{" "}
          <span className="font-semibold text-sky-700">O</span> (official leave),
          reject marks <span className="font-semibold text-red-700">A</span>{" "}
          (absent). You can decide anytime, even after the day has passed.
          {pending.length > 0 && ` · ${pending.length} pending`}
        </p>
      </div>

      <div className="space-y-3">
        {data.map((notice) => {
          const badge = APPROVAL_BADGE[notice.approval_status];

          return (
            <Card
              key={notice.id}
              className={
                notice.approval_status === "submitted"
                  ? "border-saffron-200"
                  : "opacity-90"
              }
            >
              <CardHeader className="pb-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <CardTitle className="text-base">{notice.person_name}</CardTitle>
                  <div className="flex items-center gap-2">
                    <Badge variant="notice">{notice.notice_type}</Badge>
                    <Badge variant={badge.variant}>{badge.label}</Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p>
                  <span className="text-muted-foreground">Date:</span>{" "}
                  {formatDateDisplay(notice.date)}
                </p>
                <p>
                  <span className="text-muted-foreground">Session:</span>{" "}
                  {notice.session.replace(/_/g, " ")}
                </p>
                <p>
                  <span className="text-muted-foreground">Reason:</span>{" "}
                  {notice.reason}
                </p>
                {notice.notes && (
                  <p className="text-muted-foreground">{notice.notes}</p>
                )}
                {notice.reviewed_at && notice.approval_status !== "submitted" && (
                  <p className="text-xs text-muted-foreground">
                    Reviewed {formatDateDisplay(notice.reviewed_at.slice(0, 10))}
                  </p>
                )}
                <div className="flex flex-wrap gap-2 pt-1">
                  <Button
                    size="sm"
                    variant={
                      notice.approval_status === "approved" ? "default" : "outline"
                    }
                    onClick={() =>
                      decide.mutate({ id: notice.id, decision: "approve" })
                    }
                    disabled={decide.isPending}
                  >
                    Approve (O)
                  </Button>
                  <Button
                    size="sm"
                    variant={
                      notice.approval_status === "rejected" ? "default" : "outline"
                    }
                    onClick={() =>
                      decide.mutate({ id: notice.id, decision: "reject" })
                    }
                    disabled={decide.isPending}
                  >
                    Reject (A)
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {data.length === 0 && (
        <p className="text-muted-foreground">No notices submitted yet.</p>
      )}
    </div>
  );
}
